import { Edit, Info, MoreHoriz } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { LayoutSkeletonList } from '../../lib/styles';
import type { LayoutList__Layout as Layout } from '../../queries/list.generated';
import { Menu } from '../components';

export const useColumns = (): GridColDef[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(
    () => [
      {
        field: 'id',
        ...hiddenHeader(t('common.labels.preview')),
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<Layout>) => {
          return (
            <Box display="flex" alignItems="center" justifyContent="flex-start" width="100%">
              <LayoutSkeletonList layout={row} sx={{ height: '100%', width: '75%' }} />
            </Box>
          );
        },
        minWidth: 100,
        maxWidth: 100,
      },
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
      },
      {
        field: 'kind',
        ...hiddenHeader(t('common.labels.type')),
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<Layout>) => {
          return (
            !row.canUpdate.value && (
              <Tooltip title={t('pages.layouts.tooltips.system_layout')}>
                <Chip label={t('pages.layouts.labels.system')} color="primary" />
              </Tooltip>
            )
          );
        },
        minWidth: 150,
      },
      {
        field: 'width',
        headerName: t('common.labels.width'),
        minWidth: 100,
      },
      {
        field: 'height',
        headerName: t('common.labels.height'),
        minWidth: 100,
      },
      {
        field: 'zones',
        headerName: t('pages.layouts.columns.zones'),
        sortable: false,
        renderCell: ({ value: zones }: GridRenderCellParams<Layout, Layout['zones']>) => {
          return <Chip color="primary" label={zones?.length || 0} />;
        },
        minWidth: 50,
      },
      {
        field: 'actions',
        type: 'actions',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }: GridRowParams<Layout>) => {
          const actions = [];

          if (row.canUpdate.value) {
            actions.push(
              <ActionButtonLink
                key={`edit-${row.id}`}
                title={t('pages.layouts.actions.edit_layout')}
                Icon={Edit}
                to={`${row.id}`}
              />,
            );
          } else {
            actions.push(
              <ActionButtonLink
                key={`view-${row.id}`}
                title={t('pages.layouts.actions.view_layout')}
                Icon={Info}
                to={`${row.id}`}
              />,
            );
          }

          actions.push(<Menu key={`more-action-${row.id}`} layout={row} Icon={MoreHoriz} />);

          return actions;
        },
      },
    ],
    [isSmallAndUp, t],
  );
};
